import { useQuery } from "@tanstack/vue-query";
import {
  computed,
  type MaybeRef,
  reactive,
  type Ref,
  shallowRef,
  toRef,
  unref,
  watch,
} from "vue";

import { defineStore } from "@/lib/depot";
import {
  type RouteLocation,
  type RouteLocationRaw,
  useRoute,
  useRouter,
} from "vue-router";

import type { components as InternalOpenAPI } from "@/autogen/internal-openapi";
import URLS from "@/autogen/urls";
import { mapDefined } from "@/lib/collections";
import { useLocalStorageRef } from "@/lib/composables/local-storage";
import { OkapiError } from "@/lib/openapi";
import { showToast } from "@/lib/toasts";
import { IS_DEMO } from "@/lib/environment";
import { type Feature, requiredPlan } from "@/screens/billing/lib";
import type { Account } from "@/types/account";
import type { Newsletter } from "@/types/newsletter";

declare const ACCOUNT: Account | undefined;
declare const ACTIVE_NEWSLETTER_ID: string | undefined;
declare const DEBUG: "True" | "False" | undefined;

type AccountPermission = InternalOpenAPI["schemas"]["PermissionSchema"];
type Permissions = InternalOpenAPI["schemas"]["Permissions"];

const shouldRouteRedirect = (
  route: RouteLocation
): RouteLocationRaw | undefined => {
  switch (route.name) {
    case "/search/automations.[id]":
    case "/search/subscribers.[id]":
    case "/search/tags.[id]": {
      return { name: "/search", query: route.query };
    }
    case "/inbox/[id]":
    case "/inbox/[id]/notes.new":
    case "/inbox/[id]/subscriber": {
      return { name: "/inbox" };
    }
    case "/subscribers/[id]":
    case "/subscribers/[id]/change-email-address":
    case "/subscribers/[id]/notes.new":
    case "/subscribers/imports/[id]": {
      return { name: "/subscribers" };
    }
    case "/emails.[id]":
    case "/emails.[id]/completed":
    case "/emails.[id]/details":
    case "/emails.[id]/details/analytics.[page]":
    case "/emails.[id]/drafting":
    case "/emails.[id]/history.[[historyId]]":
    case "/emails/analytics/[ids]":
    case "/emails/analytics/[ids]/[page]":
    case "/emails/imports/[id]": {
      return { name: "/emails" };
    }
    case "/automations.[id].body":
    case "/automations/[id]":
    case "/automations/[id]/analytics.[page]":
    case "/automations/[id]/delete": {
      return { name: "/automations" };
    }
    case "/tags/[id]":
    case "/tags/[id]/notes.new": {
      return { name: "/tags" };
    }
    case "/feeds.[id].body":
    case "/feeds/[id]":
    case "/feeds/[id]/delete":
    case "/feeds/[id]/notes.new": {
      return { name: "/feeds" };
    }
    case "/mentions/[id]": {
      return { name: "/mentions" };
    }
    case "/webhooks/[id]": {
      return { name: "/webhooks" };
    }
    case "/surveys/[id]":
    case "/surveys/[id]/notes.new": {
      return { name: "/surveys" };
    }
    case "/events/[id]": {
      return { name: "/events" };
    }
    case "/requests/[id]": {
      return { name: "/requests" };
    }
    case "/sponsorships/[id]": {
      return { name: "/sponsorships" };
    }
    case "/settings/files/attachments/[id]": {
      return { name: "/settings/files" };
    }
    case "/settings/integrations/memberful/plans/[id]": {
      return { name: "/settings/integrations" };
    }
    case "/settings/security/totp.[id].delete":
    case "/settings/security/totp.[id].edit": {
      return { name: "/settings/security" };
    }
    case "/settings/team/[id]/":
    case "/settings/team/[id]/delete/": {
      return { name: "/settings/team" };
    }
    case "/settings/general.description/history.[[historyId]]":
    case "/settings/email.css/history.[[historyId]]":
    case "/settings/email.footer/history.[[historyId]]":
    case "/settings/email.header/history.[[historyId]]":
    case "/settings/email.template/history.[[historyId]]": {
      if (route.params.historyId === undefined) {
        return undefined;
      }

      return { name: route.name };
    }
  }

  return undefined;
};

export const useAccountStore = defineStore("account", () => {
  const router = useRouter();
  const route = useRoute();

  // Check if the newsletter query param is present, we want to use this as
  // the active newsletter if the account has access to it.
  let OVERRIDE_NEWSLETTER_USERNAME: string | undefined;
  {
    // During this store's initialization, `route.query` is still empty.
    const url = new URL(location.href);
    const override = url.searchParams.get("newsletter");

    if (override !== null) {
      OVERRIDE_NEWSLETTER_USERNAME = override;
    }
  }

  const ACCOUNT_ID = ACCOUNT?.id;

  const { data: account } = useQuery({
    queryKey: ["account"],
    async queryFn({ signal }): Promise<Account> {
      if (!ACCOUNT_ID) {
        throw new Error(`no usable ACCOUNT_ID constant`);
      }

      const response = await fetch(`/api/emails/api/account/${ACCOUNT_ID}`, {
        signal: signal,
      });

      const json = await response.json();

      if (!response.ok) {
        throw new OkapiError(response, json, json.detail);
      }

      return json as Account;
    },
    gcTime: Infinity,
    initialData: ACCOUNT,
    initialDataUpdatedAt: performance.timeOrigin,
  });

  if (DEBUG === "False") {
    // At this point, there is no longer a need to have the initial account data
    // stored on memory any longer, remove from global.

    // @ts-expect-error
    window.ACCOUNT = undefined;
  }

  const activeNewsletterId = useLocalStorageRef("activeNewsletterId", {
    parse(value) {
      return value;
    },
    stringify(value) {
      return value;
    },
  });

  const newsletter = computed((): Newsletter | undefined => {
    const permissions = account.value?.permissions;
    if (!permissions) {
      return undefined;
    }

    const persistedId = activeNewsletterId.value;
    let matched: AccountPermission | undefined;

    if (matched === undefined && OVERRIDE_NEWSLETTER_USERNAME !== undefined) {
      matched = permissions.find((perm) => {
        return perm.newsletter.username === OVERRIDE_NEWSLETTER_USERNAME;
      });

      // No need to keep this around anymore
      OVERRIDE_NEWSLETTER_USERNAME = undefined;
    }

    if (matched === undefined && persistedId !== undefined) {
      matched = permissions.find((perm) => {
        return perm.newsletter.id === persistedId;
      });
    }

    if (matched === undefined && ACTIVE_NEWSLETTER_ID) {
      matched = permissions.find((perm) => {
        return perm.newsletter.id === ACTIVE_NEWSLETTER_ID;
      });
    }

    if (matched === undefined) {
      matched = permissions.at(0);
    }

    return matched?.newsletter;
  });

  const newsletterPermissions = computed(() => {
    const active = newsletter.value;
    if (!active) {
      return undefined;
    }

    const matched = account.value?.permissions.find((perm) => {
      return perm.newsletter.id === active.id;
    });

    return matched?.permissions ?? {};
  });

  watch(
    newsletter,
    (newsletter) => {
      if (newsletter) {
        activeNewsletterId.value = newsletter.id;
      }
    },
    { immediate: true }
  );

  const switchNewsletter = (next: string): boolean => {
    const matched = account.value?.permissions.find((perm) => {
      return perm.newsletter.id === next;
    });

    if (matched === undefined) {
      return false;
    }

    // Silently update the server-side session
    const newsletterId = matched.newsletter.legacy_id || matched.newsletter.id;

    const promise = fetch(
      URLS["internal-api:set_active_newsletter"](newsletterId),
      {
        method: "POST",
        headers: {
          authorization: `Token ${matched.api_key}`,
        },
      }
    );

    promise.catch((err) => {
      // Silently fail - this is not critical for the user experience
      console.warn("Failed to update server-side newsletter session:", err);
    });

    const redirect = shouldRouteRedirect(route);
    if (redirect !== undefined) {
      router.push(redirect);
    }

    activeNewsletterId.value = matched.newsletter.id;
    return true;
  };

  const logout = async () => {
    await fetch(URLS["auth_logout"](), { method: "DELETE" });

    location.href = "/login";

    showToast({
      type: "success",
      title: `Logged out successfully`,
      message: `Have a great day!`,
    });

    return new Promise(() => {});
  };

  return reactive({
    account,
    newsletter,
    newsletterPermissions,
    switchNewsletter,
    logout,
  });
});

type AccountStore = ReturnType<typeof useAccountStore>;

export const useMockAccountStore = defineStore("account", (): AccountStore => {
  const account = shallowRef<Account>();
  const newsletter = shallowRef<Newsletter>();

  const newsletterPermissions = computed(() => {
    const active = newsletter.value;
    if (!active) {
      return undefined;
    }

    const matched = account.value?.permissions.find((perm) => {
      return perm.newsletter.id === active.id;
    });

    return matched?.permissions ?? {};
  });

  // We can't do a direct return, otherwise reactive<T>() picks up AccountStore
  // as the generic, which would be wrong because reactive() does an unwrap
  const instance = reactive({
    account: account,
    newsletter: newsletter,
    newsletterPermissions: newsletterPermissions,
    switchNewsletter(_next: string): boolean {
      return false;
    },
    logout(): Promise<void> {
      return Promise.resolve();
    },
  });

  return instance;
});

export const useAccount = () => {
  const store = useAccountStore();
  const account = toRef(store, "account");

  if (!account.value) {
    throw new Error(`useAccount() called when account store is not ready`);
  }

  return account as Ref<Account>;
};

export const useNewsletter = () => {
  const store = useAccountStore();
  const newsletter = toRef(store, "newsletter");

  if (!newsletter.value) {
    throw new Error(`useNewsletter() called when account store is not ready`);
  }

  return newsletter as Ref<Newsletter>;
};

export const useNewsletterPermissions = () => {
  const store = useAccountStore();
  const permissions = toRef(store, "newsletterPermissions");

  if (!permissions.value) {
    throw new Error(
      `useNewsletterPermissions() called when account store is not ready`
    );
  }

  return permissions as Ref<Permissions>;
};

export const useActionPermitted = (
  key: MaybeRef<keyof Permissions | undefined>,
  level: "read" | "write"
) => {
  const permissions = useNewsletterPermissions();

  const permitted = computed((): boolean => {
    const $key = unref(key);
    if ($key === undefined) {
      return true;
    }

    const permission = permissions.value[$key];
    switch (level) {
      case "read": {
        return permission === "read" || permission === "write";
      }
      case "write": {
        return permission === "write";
      }
    }

    return false;
  });

  return permitted;
};

export const useFeatureRequirement = (
  feature: MaybeRef<Feature | undefined>
) => {
  const account = useAccount();
  const newsletter = useNewsletter();

  const requirement = computed(() => {
    const $feature = unref(feature);
    if ($feature === undefined) {
      return null;
    }

    if (IS_DEMO) {
      return null;
    }

    // We need to check the billing plan and the feature set of the newsletter's
    // owner, who might not be the current user.
    const accountWithOwningAccountBillingPlan: Account = {
      ...account.value,
      billing_plan:
        newsletter.value.owning_account_billing_plan ??
        account.value.billing_plan,
      billing_type:
        newsletter.value.owning_account_billing_type ??
        account.value.billing_type,
      features:
        (newsletter.value.owning_account_features as Account["features"]) ??
        account.value.features,
    };

    return requiredPlan(accountWithOwningAccountBillingPlan, $feature);
  });

  return requirement;
};

export const useFeaturePermitted = (feature: MaybeRef<Feature | undefined>) => {
  const requirement = useFeatureRequirement(feature);

  const permitted = computed(() => {
    return requirement.value === null;
  });

  return permitted;
};

export const useOAuthProviders = () => {
  const account = useAccount();

  const providers = computed(() => {
    const connections = mapDefined(account.value.connections, (conn) => {
      switch (conn.provider) {
        case "apple":
        case "google": {
          return conn.provider;
        }
      }
    });

    if (connections.length === 0) {
      return null;
    }

    return connections;
  });

  return providers;
};
